import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Download } from "lucide-react";
import { downloadCertificate } from "@/components/site/Certificate";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MaskLines, Reveal } from "@/components/site/Reveal";

const STATUS = { pending: "bg-amber/20 text-ink", approved: "bg-gum text-sand", declined: "bg-ink/10 text-ink/70", completed: "bg-ink text-sand" };

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const load = () => api.get("/enrolments/me").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const withdraw = async (id) => {
    try { await api.delete(`/enrolments/${id}`); toast.success("Enrolment withdrawn"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <main className="min-h-screen bg-sand px-5 pb-24 pt-36 sm:px-10">
      <div className="mx-auto max-w-[1200px]">
        <p className="eyebrow text-ochre">Student dashboard</p>
        <MaskLines lines={[`Hello, ${user?.name?.split(" ")[0] || "there"}.`]} className="mt-4 font-display text-5xl font-medium tracking-tight sm:text-7xl" />
        <div className="mt-16 flex items-end justify-between border-b border-ink/15 pb-4">
          <h2 className="font-display text-3xl">My enrolments</h2>
          <Link to="/programmes" data-testid="dashboard-browse-link" className="flex items-center gap-1 text-sm font-semibold text-ochre">Browse programmes <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
        {items && !items.length && (
          <p data-testid="dashboard-empty" className="py-16 text-ink/70">You haven't enrolled in a programme yet.</p>
        )}
        {(items || []).map((e, i) => (
          <Reveal key={e.id} delay={i * 0.05}>
            <div data-testid={`enrolment-${e.course_slug}`} className="flex flex-col gap-4 border-b border-ink/15 py-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <Link to={`/programmes/${e.course_slug}`} className="font-display text-3xl hover:text-ochre">{e.course_title}</Link>
                <p className="mt-1 text-xs text-ink/65">Enrolled {new Date(e.created_at).toLocaleDateString()}</p>
                {e.status === "completed" && e.completed_on && <p data-testid={`dashboard-completed-${e.course_slug}`} className="mt-1 text-xs text-ink/75">Completed {new Date(`${e.completed_on}T00:00:00`).toLocaleDateString("en-AU")}{e.renew_by && <> · <span className="font-semibold text-ochre-dark">Renew by {new Date(`${e.renew_by}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</span></>}</p>}
                {e.needs_transport && <p data-testid={`dashboard-transport-${e.course_slug}`} className="mt-1 text-xs text-ink/75">Lift for {e.transport_for?.toLowerCase()} from {e.pickup_suburb}{e.session_label ? ` (${e.session_label})` : ""} — <span className="font-mono uppercase">{(e.transport_status || "requested").replace("_", " ")}</span></p>}
              </div>
              <div className="flex items-center gap-4">
                <span data-testid={`enrolment-status-${e.course_slug}`} className={`rounded-full px-4 py-1.5 font-mono text-xs uppercase tracking-wider ${STATUS[e.status] || ""}`}>{e.status}</span>
                {e.certificate && <button data-testid={`download-cert-${e.course_slug}`} onClick={() => downloadCertificate(e.id, e.certificate.filename)} className="flex items-center gap-2 rounded-full bg-ochre px-4 py-2 text-sm font-semibold text-sand transition-colors hover:bg-ochre-dark"><Download className="h-4 w-4" /> Certificate</button>}
                {e.status !== "completed" && <button data-testid={`withdraw-${e.course_slug}`} onClick={() => withdraw(e.id)} className="text-sm text-ink/70 underline underline-offset-4 hover:text-ochre">Withdraw</button>}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </main>
  );
}
